<template>
  <div>
    <form>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <h1>Profile Image &amp; Marketing Photos</h1>

          <a @click="$router.go(-1)" class="btn btn-info" active-class="">
            <i class="fa fa-chevron-left"></i> Back
          </a>
        </div>
      </div>

      <div class="container-fluid">
        <div class="dashboard-block">
          <h2>Profile Picture</h2>

          <div class="panel panel-default">
            <div class="panel-body">
              <div class="row">
                <div class="col-lg-6">
                  <div class="form-horizontal">
                    <div :class="['form-group', errorClass('profile_image')]">
                      <label class="control-label col-sm-4">Upload Image</label>
                      <div class="col-sm-8">
                        <div class="file-upload-control">
                          <input
                            name="profile_image"
                            id="profile_image"
                            type="file"
                            @change="handleFileSelectionCallback"
                            @input="handleFileSelectionCallback">
                          <label for="profile_image" class="btn btn-default btn-block">Select Profile Picture</label>
                        </div>

                        <p class="help-block">For best results images should be 512px by 512px.</p>

                        <help-block field="profile_image"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div style="width: 100%">
                    <img id="profile-image-crop" :src="userProfileImageOriginalUrl">
                  </div>
                </div>
              </div>
            </div>
            <div class="panel-footer flex justify-between items-center">
              <ladda-button
                type="submit"
                class="btn btn-primary"
                :click="handleSaveProfileImageCallback">
                Save Profile Picture
              </ladda-button>
              <a class="btn btn-primary" :href="userProfileImageOriginalUrl" target="_blank" download>
                  <i class="fa fa-cloud-download fa-lg" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </form>

<!--    Start of Marketing Photos section -->

    <div class="container-fluid">
      <h2>Marketing Photos<br>
        <small class="subtext">Upload three professional photos that clients can use for event and marketing promotions. Please ensure these images are not branded in team/club attire and you have the required permissions to allow clients to use these photos.</small>
      </h2>
        <div class="row">
          <div v-for="i in 3" :key="i" class="col-xs-12 col-sm-4 margin-tb">
            <talent-marketing-photo :key="i" :index="i" :image="images[i-1]" @upload="(photo) => {images.push(photo)}" @delete="() => {images.splice(i-1, 1)}"></talent-marketing-photo>
          </div>
        </div>
   </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { pick } from 'lodash'
import { helpers } from 'mixins/users'
import { notify } from 'utils'
import { handleServerErrors } from 'utils/http'
import Cropper from 'cropperjs'
import inView from 'in-view'
import TalentMarketingPhoto from '../talent/TalentMarketingPhoto'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  components: {
    TalentMarketingPhoto
  },

  computed: {
    ...mapState('user', ['user'])
  },

  data () {
    return {
      cropperInstance: null,
      imageContainer: null,
      file: '',
      cropPayload: {},
      images: []
    }
  },

  watch: {
    user: 'loadMarketingPhotos'
  },

  mounted () {
    if (this.user.id !== undefined) {
      this.loadMarketingPhotos()
    }

    this.imageContainer = document.getElementById('profile-image-crop')

    // Reset validator errors after the image is ready. This is necessary due to a weird issue
    // where resetting the errors before replacing the cropper image causes the image to not
    // be replaced when there are actually errors to reset.
    this.imageContainer.addEventListener('ready', () => {
      this.$validator.reset()
    })

    inView('#profile-image-crop')
      .once('enter', this.createCropperInstance)
  },

  methods: {
    ...mapActions('talent/profile', ['getTalentMarketingPhotos']),
    ...mapActions('user', ['uploadUserProfileImage']),

    /**
     * Make api call to get talent's marketing photos
     *
     * @return void
     */
    loadMarketingPhotos () {
      this.getTalentMarketingPhotos(this.user.id)
        .then(response => {
          this.images = response
        })
    },

    /**
     * Create the cropper instance.
     *
     * @return void
     */
    createCropperInstance () {
      this.cropperInstance = new Cropper(this.imageContainer, {
        aspectRatio: 1,
        zoomable: false,
        viewMode: 1,
        data: this.user.profile_image_crop_payload,
        crop: (event) => {
          this.cropPayload = pick(event.detail, ['x', 'y', 'width', 'height', 'scaleX', 'scaleY'])
        }
      })
    },

    /**
     * Handle file selection.
     *
     * This pulls the file from the input box and sets it on the cropper instance.
     *
     * @return void
     */
    handleFileSelectionCallback ({ target: { files } }) {
      this.errors.clear()

      if (files.length === 0) {
        this.file = ''

        return
      }

      this.file = files.item(0)

      if (!/^image\/\w+/.test(this.file.type)) {
        this.file = ''

        this.imageContainer.src = ''

        this.cropperInstance.destroy()

        this.$nextTick(() => {
          this.errors.add('profile_image', 'The file you have selected is an invalid image type.')
        })

        return
      }

      this.cropperInstance.replace(URL.createObjectURL(this.file))
    },

    /**
     * Handle saving the profile image to the server with the crop payload.
     *
     * @return Promise
     */
    handleSaveProfileImageCallback () {
      let payload = {
        id: this.user.id,
        file: this.file,
        cropPayload: this.cropPayload
      }

      return this.uploadUserProfileImage(payload)
        .then(() => {
          notify.success('Successfully saved profile image.')
          this.trackEvent(this.analytics.V2.AccountSectionEditEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.IMAGE })
          this.trackEvent(this.analytics.V2.AccountProfileImageUploadEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.IMAGE })
          this.$validator.reset()
        })
        .catch((errors) => {
          if (errors.response) {
            handleServerErrors(errors, this.errors)
          } else {
            notify.error('Sorry something went wrong saving your image, please try again.')
            throw errors
          }
        })
    }
  }
}
</script>
